import { useMemo, useState } from 'react';
import styles from '../../../../assets/styles/Admin/AddProduct.module.scss';
import ProductForm1 from './ProductForm1';
import ProductForm2 from './ProductForm2';
import ProductForm3 from './ProductForm3';
import { MimaButton } from '../../../../components';

const sections = [
	{ id: 1, title: 'Product Info' },
	{ id: 2, title: 'Pricing & Variations' },
	{ id: 3, title: 'Media & Visibility' },
];

const countErrors = value => {
	if (!value) return 0;
	if (typeof value === 'string') return 1;
	if (Array.isArray(value)) {
		return value.reduce((total, item) => total + countErrors(item), 0);
	}
	if (typeof value === 'object') {
		return Object.values(value).reduce(
			(total, item) => total + countErrors(item),
			0
		);
	}

	return 0;
};

const ProductForm = ({ formik }) => {
	const [activeSection, setActiveSection] = useState(1);
	const [visitedSections, setVisitedSections] = useState(new Set([1]));

	const { values, errors, submitCount, isSubmitting } = formik;

	const completedSections = useMemo(() => {
		const completed = [];

		const sectionOneComplete =
			Boolean(values?.name) &&
			Boolean(values?.gender) &&
			Boolean(values?.type) &&
			(values?.createCollections
				? values?.newCollections?.length > 0
				: values?.styleCollections?.length > 0);

		if (sectionOneComplete) completed.push(1);

		const sectionTwoComplete =
			Boolean(values?.pricingModel) &&
			Array.isArray(values?.variations) &&
			values.variations.length > 0 &&
			values.variations.every(variation => Boolean(variation?.name));

		if (sectionTwoComplete) completed.push(2);

		const sectionThreeComplete =
			Array.isArray(values?.imageUrls) && values.imageUrls.length > 0;

		if (sectionThreeComplete) completed.push(3);

		return completed;
	}, [values]);

	const progress = Math.round(
		(completedSections.length / sections.length) * 100
	);

	const sectionErrors = useMemo(() => {
		return {
			1: countErrors({
				name: errors?.name,
				gender: errors?.gender,
				type: errors?.type,
				styleCollections: errors?.styleCollections,
				newCollections: errors?.newCollections,
			}),
			2: countErrors({
				pricingModel: errors?.pricingModel,
				sellAbroad: errors?.sellAbroad,
				variations: errors?.variations,
			}),
			3: countErrors({
				imageUrls: errors?.imageUrls,
				availableWindow: errors?.availableWindow,
				availableDays: errors?.availableDays,
				daysRequiredForDelivery: errors?.daysRequiredForDelivery,
			}),
		};
	}, [errors]);

	const totalIssues = useMemo(() => {
		return Object.values(sectionErrors).reduce(
			(total, issueCount) => total + issueCount,
			0
		);
	}, [sectionErrors]);

	const erroredSections = useMemo(() => {
		return sections
			.filter(section => (sectionErrors?.[section.id] || 0) > 0)
			.map(section => ({
				...section,
				issueCount: sectionErrors?.[section.id] || 0,
			}));
	}, [sectionErrors]);

	const toggleSection = id => {
		setVisitedSections(prev => {
			const next = new Set(prev);
			next.add(id);
			return next;
		});
		setActiveSection(prev => (prev === id ? null : id));
	};

	const goToSection = id => {
		setVisitedSections(prev => {
			const next = new Set(prev);
			next.add(id);
			return next;
		});
		setActiveSection(id);
	};

	return (
		<div className={styles.addProduct}>
			<div className={styles.addProduct__topRail}>
				<div className={styles.addProduct__tracker}>
					<div className={styles.addProduct__trackerHeader}>
						<strong>Form completion</strong>
						<span>{progress}%</span>
					</div>
					<div className={styles.addProduct__progressBar}>
						<div
							className={styles.addProduct__progressFill}
							style={{ width: `${progress}%` }}
						/>
					</div>
				</div>

				{submitCount > 0 && totalIssues > 0 ? (
					<div className={styles.addProduct__errorCard}>
						<div className={styles.addProduct__errorCardTitle}>
							Please fix {totalIssues} issue{totalIssues > 1 ? 's' : ''}
						</div>
						<div className={styles.addProduct__errorChips}>
							{erroredSections.map(section => (
								<button
									type="button"
									key={section.id}
									onClick={() => goToSection(section.id)}
									className={styles.addProduct__errorChip}>
									{section.title} ({section.issueCount})
								</button>
							))}
						</div>
					</div>
				) : null}
			</div>

			<form onSubmit={formik.handleSubmit}>
				<div className={styles.addProduct__accordion}>
					{sections.map(section => {
						const isOpen = activeSection === section.id;
						const isCompleted = completedSections.includes(section.id);
						const issueCount = sectionErrors?.[section.id] || 0;
						const showIssues =
							submitCount > 0 || visitedSections.has(section.id);

						return (
							<div className={styles.addProduct__item} key={section.id}>
								<button
									type="button"
									onClick={() => toggleSection(section.id)}
									className={styles.addProduct__trigger}>
									<span>{section.title}</span>
									<span
										className={`${styles.addProduct__status} ${
											showIssues && issueCount > 0
												? styles.addProduct__statusPending
												: isCompleted
													? styles.addProduct__statusDone
													: styles.addProduct__statusPending
										}`}>
										{showIssues && issueCount > 0
											? `${issueCount} issue${issueCount > 1 ? 's' : ''}`
											: isCompleted
												? 'Complete'
												: 'Pending'}
									</span>
								</button>

								{isOpen && (
									<div className={styles.addProduct__panel}>
										{section.id === 1 && <ProductForm1 formik={formik} />}
										{section.id === 2 && <ProductForm2 formik={formik} />}
										{section.id === 3 && <ProductForm3 formik={formik} />}
									</div>
								)}
							</div>
						);
					})}
					<div className={styles.addProduct__actions}>
						<MimaButton
							title={'Save Product'}
							type="submit"
							variant="colorableSmall"
							buttonColor="var(--color-fash-black)"
							loading={isSubmitting}
							disabled={isSubmitting}
						/>
					</div>
				</div>
			</form>
		</div>
	);
};

export default ProductForm;
